import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileSpreadsheet, Upload, Search, Trash2 } from 'lucide-react';
import {
  m2LinhaCalcPlanilha,
  mesclarPorLinhaGrupos,
  parseArquivoCalcPlanilha,
  recalcularGruposPlanilha,
  rotuloCompletoLinhaCalc,
  rotuloGrupoDasLinhas,
  type GrupoCalcPlanilha,
  type LinhaCalcPlanilha,
} from '../../utils/calculadoraPlanilhaExcel';
import { hidratarCalcPlanilha, salvarCalcPlanilha } from '../../utils/calculadoraPlanilhaPersistencia';

interface PontuacaoMini {
  nome: string;
  m2: number;
}

interface Props {
  pontuacoesProdutos: PontuacaoMini[];
}

function fmt(n: number, casas = 2): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { maximumFractionDigits: casas, minimumFractionDigits: 0 });
}

const CORES_GRUPO = [
  'bg-emerald-50',
  'bg-sky-50',
  'bg-amber-50',
  'bg-violet-50',
  'bg-rose-50',
];

export default function CalculadoraPlanilhaSection({ pontuacoesProdutos }: Props) {
  const [linhas, setLinhas] = useState<LinhaCalcPlanilha[]>([]);
  const [meta, setMeta] = useState(725);
  const [metaTexto, setMetaTexto] = useState('725');
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [filtro, setFiltro] = useState('');
  const [grupos, setGrupos] = useState<GrupoCalcPlanilha[]>([]);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [estSegurDigitando, setEstSegurDigitando] = useState<Record<string, string>>({});
  const prontoParaSalvar = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      const e = await hidratarCalcPlanilha();
      if (cancelado) return;
      setLinhas(e.linhas);
      const m = Number(e.meta);
      setMeta(Number.isFinite(m) && m > 0 ? m : 725);
      setMetaTexto(String(Number.isFinite(m) && m > 0 ? m : 725));
      setNomeArquivo(e.nomeArquivo || '');
      setGrupos(Array.isArray(e.grupos) ? e.grupos : []);
      prontoParaSalvar.current = true;
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!prontoParaSalvar.current) return;
    const t = window.setTimeout(() => {
      void salvarCalcPlanilha({
        linhas,
        meta,
        nomeArquivo,
        salvoEm: '',
        grupos,
      });
    }, 400);
    return () => window.clearTimeout(t);
  }, [linhas, meta, nomeArquivo, grupos]);

  const nomesPontuacao = useMemo(
    () => pontuacoesProdutos.map((p) => p.nome),
    [pontuacoesProdutos]
  );

  const idsEmGrupo = useMemo(() => {
    const s = new Set<string>();
    for (const g of grupos) for (const id of g.idsLinhas) s.add(id);
    return s;
  }, [grupos]);

  const idsSelecionadosLivres = useMemo(
    () => linhas.filter((l) => l.selecionada && !idsEmGrupo.has(l.id)).map((l) => l.id),
    [linhas, idsEmGrupo]
  );

  const gruposFonte = useMemo(() => {
    if (idsSelecionadosLivres.length === 0) return grupos;
    return [
      ...grupos,
      {
        id: '__previa',
        idsLinhas: [...idsSelecionadosLivres],
        rotulo: 'Prévia',
        metaDoGrupo: meta,
      },
    ];
  }, [grupos, idsSelecionadosLivres, meta]);

  const simulacao = useMemo(
    () => recalcularGruposPlanilha(linhas, gruposFonte, pontuacoesProdutos, meta),
    [linhas, gruposFonte, pontuacoesProdutos, meta]
  );

  const gruposCalc = useMemo(
    () => simulacao.filter((g) => g.id !== '__previa'),
    [simulacao]
  );
  const previaGrupo = useMemo(
    () => simulacao.find((g) => g.id === '__previa') ?? null,
    [simulacao]
  );
  const previa = previaGrupo?.resultado ?? null;

  const grupoPorLinha = useMemo(() => {
    const m = new Map<string, (typeof gruposCalc)[number]>();
    gruposCalc.forEach((g) => {
      for (const id of g.idsLinhas) m.set(id, g);
    });
    return m;
  }, [gruposCalc]);

  const totalGrupos = useMemo(
    () => gruposCalc.reduce((s, g) => s + (g.resultado?.total ?? 0), 0),
    [gruposCalc]
  );

  const porLinha = useMemo(() => mesclarPorLinhaGrupos(simulacao, null), [simulacao]);

  const visiveis = useMemo(() => {
    const f = filtro.trim().toUpperCase();
    if (!f) return linhas;
    return linhas.filter((l) =>
      `${rotuloCompletoLinhaCalc(l)} ${l.produtoNome} ${l.corProduto} ${l.codigoProduto}`
        .toUpperCase()
        .includes(f)
    );
  }, [linhas, filtro]);

  const importar = useCallback(
    async (file: File) => {
      const buf = await file.arrayBuffer();
      const parsed = parseArquivoCalcPlanilha(buf, file.name, nomesPontuacao);
      setLinhas(parsed.linhas);
      setNomeArquivo(parsed.nomeArquivo);
      setGrupos([]);
      setAvisos(parsed.avisos);
      if (parsed.linhas.length > 0) {
        setAvisos((a) => [
          ...a,
          `${parsed.linhas.length} linha(s) importada(s). A caixa de meta vale para o grupo que você for fechar. Feche o grupo 1 com 725; depois mude a caixa (ou deixe 725) e feche o grupo 2.`,
        ]);
      }
    },
    [nomesPontuacao]
  );

  const atualizarLinha = (id: string, patch: Partial<LinhaCalcPlanilha>) => {
    setLinhas((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const apagarSelecionadas = () => {
    const n = linhas.filter((l) => l.selecionada).length;
    if (n === 0) {
      alert('Marque o visto das linhas que quer apagar.');
      return;
    }
    if (!confirm(`Apagar ${n} linha(s) marcada(s)?`)) return;
    const ids = new Set(linhas.filter((l) => l.selecionada).map((l) => l.id));
    setLinhas((prev) => prev.filter((l) => !l.selecionada));
    setGrupos((prev) =>
      prev
        .map((g) => ({ ...g, idsLinhas: g.idsLinhas.filter((id) => !ids.has(id)) }))
        .filter((g) => g.idsLinhas.length > 0)
    );
  };

  const apagarLinha = (id: string) => {
    setLinhas((prev) => prev.filter((l) => l.id !== id));
    setGrupos((prev) =>
      prev
        .map((g) => ({ ...g, idsLinhas: g.idsLinhas.filter((x) => x !== id) }))
        .filter((g) => g.idsLinhas.length > 0)
    );
  };

  const apagarTodaPlanilha = () => {
    if (linhas.length === 0) return;
    if (!confirm(`Apagar as ${linhas.length} linha(s) da planilha?`)) return;
    setLinhas([]);
    setNomeArquivo('');
    setGrupos([]);
    setAvisos([]);
  };

  const fecharGrupo = () => {
    if (idsSelecionadosLivres.length === 0) {
      alert('Marque os produtos deste grupo (quem ainda não está em outro grupo).');
      return;
    }
    if (!(Number(meta) > 0)) {
      alert('Informe uma meta de pontuação maior que zero.');
      return;
    }
    const rotulo = rotuloGrupoDasLinhas(linhas, idsSelecionadosLivres);
    const novo: GrupoCalcPlanilha = {
      id: `g-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      idsLinhas: [...idsSelecionadosLivres],
      rotulo,
      metaDoGrupo: meta,
    };
    setGrupos((prev) => [...prev, novo]);
    const ids = new Set(idsSelecionadosLivres);
    setLinhas((prev) => prev.map((l) => (ids.has(l.id) ? { ...l, selecionada: false } : l)));
  };

  const desfazerUltimoGrupo = () => {
    setGrupos((prev) => prev.slice(0, -1));
  };

  const removerGrupo = (id: string) => {
    setGrupos((prev) => prev.filter((g) => g.id !== id));
  };

  const limparGrupos = () => {
    if (grupos.length === 0) return;
    setGrupos([]);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-700">
        Importe a planilha (nome + cor, vendas 90 dias, <strong>Est.Segur. = dia atual</strong>, pode ser negativo).
        A caixa de <strong>meta</strong> é a pontuação <strong>daquele grupo</strong>: feche o grupo 1 com 725;
        o que estiver na caixa na hora do grupo 2 é a meta do grupo 2 (725 de novo, ou outro valor). Não se
        reparte. Qtd já entra abate pontos daquele grupo.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) void importar(f);
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="px-4 py-2 ade-btn-gold relative z-10 font-semibold rounded-xl text-sm flex items-center gap-2"
        >
          <Upload className="w-4 h-4" /> Importar Excel
        </button>
        {nomeArquivo && (
          <span className="text-xs text-neutral-600 flex items-center gap-1">
            <FileSpreadsheet className="w-3.5 h-3.5" />
            {nomeArquivo} · {linhas.length} linha(s)
          </span>
        )}
        <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
          <label className="block text-xs font-medium text-amber-800 mb-1">
            Meta do próximo grupo
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={metaTexto}
            onChange={(e) => {
              const raw = String(e.target.value).replace(',', '.').trim();
              if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
              setMetaTexto(raw);
              const n = parseFloat(raw);
              if (Number.isFinite(n) && n >= 0) setMeta(n);
            }}
            className="w-40 px-3 py-2 rounded-lg border border-amber-300 text-lg font-bold"
            title="Vale só para o grupo que você fechar agora. Os grupos já fechados mantêm a meta da hora em que foram fechados."
          />
          <p className="text-[11px] text-amber-900 mt-1">
            Ao fechar, este valor fica gravado nesse grupo.
            {grupos.length > 0 ? ` Próximo = grupo ${grupos.length + 1}.` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={fecharGrupo}
          className="px-5 py-2.5 ade-btn-gold relative z-10 font-semibold rounded-xl text-sm"
        >
          Fechar grupo
        </button>
        <button
          type="button"
          onClick={desfazerUltimoGrupo}
          disabled={grupos.length === 0}
          className="px-4 py-2 relative z-10 font-semibold rounded-xl text-sm bg-[#e4e6ec] disabled:opacity-40"
        >
          Desfazer último grupo
        </button>
        <button
          type="button"
          onClick={apagarSelecionadas}
          className="px-4 py-2 relative z-10 font-semibold rounded-xl text-sm bg-red-100 text-red-800 border border-red-200"
        >
          Apagar selecionadas
        </button>
        <button
          type="button"
          onClick={apagarTodaPlanilha}
          className="px-4 py-2 relative z-10 font-semibold rounded-xl text-sm bg-neutral-200 text-neutral-800"
        >
          Apagar toda a planilha
        </button>
      </div>

      {avisos.length > 0 && (
        <ul className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 space-y-0.5">
          {avisos.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[12rem]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-neutral-400" />
          <input
            type="text"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Filtrar produto ou cor…"
            className="w-full pl-7 pr-2 py-1.5 text-sm rounded-lg border border-[#009b3a]/22"
          />
        </div>
        <button
          type="button"
          className="px-3 py-1.5 text-xs rounded-lg bg-[#e4e6ec] font-semibold"
          onClick={() => {
            const ids = new Set(
              visiveis.filter((l) => !idsEmGrupo.has(l.id)).map((l) => l.id)
            );
            setLinhas((prev) => prev.map((l) => (ids.has(l.id) ? { ...l, selecionada: true } : l)));
          }}
        >
          Marcar visíveis
        </button>
        <button
          type="button"
          className="px-3 py-1.5 text-xs rounded-lg bg-[#e4e6ec] font-semibold"
          onClick={() => {
            const ids = new Set(visiveis.map((l) => l.id));
            setLinhas((prev) => prev.map((l) => (ids.has(l.id) ? { ...l, selecionada: false } : l)));
          }}
        >
          Desmarcar visíveis
        </button>
      </div>

      {gruposCalc.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-black">Grupos fechados (cada um iguala os próprios dias)</p>
            <button
              type="button"
              className="px-2 py-1 text-xs rounded-lg bg-[#e4e6ec] font-semibold"
              onClick={limparGrupos}
            >
              Limpar grupos
            </button>
          </div>
          {gruposCalc.map((g) => (
            <div
              key={g.id}
              className={`rounded-xl border border-[#009b3a]/22 px-3 py-2 ${CORES_GRUPO[(g.ordem - 1) % CORES_GRUPO.length]}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-black">
                  Grupo {g.ordem}: {g.rotulo}
                </p>
                <button
                  type="button"
                  className="text-xs text-red-800 font-semibold"
                  onClick={() => removerGrupo(g.id)}
                >
                  Tirar grupo
                </button>
              </div>
              <p className="text-xs text-neutral-700">
                Meta deste grupo: <strong>{fmt(g.metaDoGrupo)}</strong>
                {' · '}
                Total: <strong>{g.resultado ? fmt(g.resultado.total) : '—'}</strong>
                {' · '}
                {g.idsLinhas.length} item(ns)
              </p>
            </div>
          ))}
        </div>
      )}

      {previa && idsSelecionadosLivres.length > 0 && (
        <p className="text-xs text-[#009b3a]">
          Prévia do próximo grupo (meta {fmt(meta)}): total <strong>{fmt(previa.total)}</strong>. Fechar
          grupo grava essa meta só neste grupo.
        </p>
      )}

      <div className="overflow-x-auto max-h-[32rem] overflow-y-auto rounded-xl border border-[#009b3a]/22">
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 bg-[#e4e6ec] z-10">
            <tr>
              <th className="border border-[#009b3a]/22 px-1 py-1 w-8">✓</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 w-14">Grupo</th>
              <th className="border border-[#009b3a]/22 px-2 py-1 text-left">Produto (nome + cor + código)</th>
              <th className="border border-[#009b3a]/22 px-2 py-1 text-left">Cor</th>
              <th className="border border-[#009b3a]/22 px-2 py-1 text-left">Código</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 w-24">Vendas 90d</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 w-24">Dia atual (Est.Segur.)</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 w-24">Qtd já entra</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 w-14">m²</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 w-20">Dia ajust.</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 w-16">Qtd</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 w-20">Pontos</th>
              <th className="border border-[#009b3a]/22 px-1 py-1 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {visiveis.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-3 py-8 text-center text-neutral-500">
                  Importe o Excel para listar os produtos.
                </td>
              </tr>
            ) : (
              visiveis.map((l) => {
                const m2 = m2LinhaCalcPlanilha(l, pontuacoesProdutos);
                const res = porLinha[l.id];
                const g = grupoPorLinha.get(l.id);
                const emGrupo = Boolean(g);
                return (
                  <tr
                    key={l.id}
                    className={
                      emGrupo
                        ? CORES_GRUPO[((g?.ordem ?? 1) - 1) % CORES_GRUPO.length]
                        : l.selecionada
                          ? 'bg-[#f0faf3]'
                          : 'bg-[#fafbfc]'
                    }
                  >
                    <td className="border border-[#009b3a]/22 text-center">
                      <input
                        type="checkbox"
                        checked={emGrupo ? true : l.selecionada}
                        disabled={emGrupo}
                        title={emGrupo ? 'Já está em um grupo fechado' : undefined}
                        onChange={(e) => atualizarLinha(l.id, { selecionada: e.target.checked })}
                      />
                    </td>
                    <td className="border border-[#009b3a]/22 text-center font-semibold">
                      {g ? g.ordem : l.selecionada ? '…' : '—'}
                    </td>
                    <td className="border border-[#009b3a]/22 px-2 py-0.5">
                      <span className="font-medium">{rotuloCompletoLinhaCalc(l)}</span>
                      {m2 <= 0 && (
                        <span className="ml-1 text-amber-700">(sem m²?)</span>
                      )}
                    </td>
                    <td className="border border-[#009b3a]/22 px-2 py-0.5 text-neutral-700">
                      {l.corProduto || '—'}
                    </td>
                    <td className="border border-[#009b3a]/22 px-2 py-0.5 font-mono text-neutral-700">
                      {l.codigoProduto || '—'}
                    </td>
                    <td className="border border-[#009b3a]/22 p-0">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={l.vendas90 || ''}
                        onChange={(e) =>
                          atualizarLinha(l.id, {
                            vendas90: parseFloat(String(e.target.value).replace(',', '.')) || 0,
                          })
                        }
                        className="w-full px-1 py-1 text-center border-0 bg-transparent"
                      />
                    </td>
                    <td className="border border-[#009b3a]/22 p-0">
                      <input
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        value={
                          estSegurDigitando[l.id] !== undefined
                            ? estSegurDigitando[l.id]
                            : Number.isFinite(l.estSegur)
                              ? String(l.estSegur)
                              : ''
                        }
                        onFocus={(e) => {
                          setEstSegurDigitando((prev) => ({
                            ...prev,
                            [l.id]: Number.isFinite(l.estSegur) ? String(l.estSegur) : '',
                          }));
                          e.currentTarget.select();
                        }}
                        onChange={(e) => {
                          const raw = String(e.target.value).replace(',', '.');
                          if (raw !== '' && raw !== '-' && !/^-?\d*\.?\d*$/.test(raw)) return;
                          setEstSegurDigitando((prev) => ({ ...prev, [l.id]: raw }));
                          if (raw === '' || raw === '-') {
                            atualizarLinha(l.id, { estSegur: 0 });
                            return;
                          }
                          const n = parseFloat(raw);
                          if (Number.isFinite(n)) atualizarLinha(l.id, { estSegur: n });
                        }}
                        onBlur={() => {
                          setEstSegurDigitando((prev) => {
                            const next = { ...prev };
                            delete next[l.id];
                            return next;
                          });
                        }}
                        title="Dia atual (negativo = atraso). Digite o menos, ex.: -20"
                        className="w-full px-1 py-1 text-center border-0 bg-transparent"
                      />
                    </td>
                    <td className="border border-[#009b3a]/22 p-0">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={l.qtdJaEntra || ''}
                        onChange={(e) =>
                          atualizarLinha(l.id, {
                            qtdJaEntra: parseInt(String(e.target.value).replace(/\D/g, ''), 10) || 0,
                          })
                        }
                        placeholder="0"
                        title="Quantidade já definida nesta remessa (não iguala dias)"
                        className="w-full px-1 py-1 text-center border-0 bg-amber-50/80"
                      />
                    </td>
                    <td className="border border-[#009b3a]/22 text-center bg-[#eef0f4]">
                      {m2 > 0 ? fmt(m2) : '—'}
                    </td>
                    <td className="border border-[#009b3a]/22 text-center font-semibold bg-[#f0faf3]">
                      {res?.diaAjustado != null ? fmt(res.diaAjustado, 1) : '—'}
                    </td>
                    <td className="border border-[#009b3a]/22 text-center font-semibold bg-emerald-50">
                      {res ? res.quantidade : '—'}
                    </td>
                    <td className="border border-[#009b3a]/22 text-center bg-amber-50">
                      {res ? fmt(res.pontos) : '—'}
                    </td>
                    <td className="border border-[#009b3a]/22 text-center">
                      <button
                        type="button"
                        title="Apagar esta linha"
                        className="p-1 text-red-700 hover:bg-red-50 rounded"
                        onClick={() => apagarLinha(l.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5 inline" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {(gruposCalc.length > 0 || previa) && (
        <div className="p-4 bg-[#e8f5ec] rounded-xl border-2 border-[#009b3a]/28 space-y-2">
          <h4 className="font-bold text-black text-lg">Total da remessa</h4>
          {(() => {
            const totalGeral = totalGrupos + (previa?.total ?? 0);
            return (
              <>
                <p className="text-2xl font-bold text-black">{fmt(totalGeral)}</p>
                <p className="text-sm text-[#009b3a]">
                  Soma dos grupos (cada um com a própria meta da caixa na hora em que fechou)
                </p>
                <ul className="text-xs text-black space-y-0.5">
                  {gruposCalc.map((g) => {
                    const tot = g.resultado?.total ?? 0;
                    const acima = tot - g.metaDoGrupo;
                    return (
                      <li key={g.id}>
                        Grupo {g.ordem}: meta {fmt(g.metaDoGrupo)} → {fmt(tot)}
                        {acima >= -0.05 ? ` (${fmt(Math.max(0, acima))} acima)` : ''}
                      </li>
                    );
                  })}
                  {previa ? (
                    <li>
                      Prévia: meta {fmt(meta)} → {fmt(previa.total)}
                    </li>
                  ) : null}
                </ul>
              </>
            );
          })()}
          <div className="rounded-lg bg-[#fafbfc] border border-[#009b3a]/18 px-3 py-2 space-y-1">
            <p className="text-xs font-semibold text-black">Como ficaram os dias</p>
            {gruposCalc.map((g) => {
              const dias = g.idsLinhas
                .map((id) => {
                  const l = linhas.find((x) => x.id === id);
                  const r = g.resultado?.porLinha[id];
                  if (!l || !r || r.modo === 'fora') return null;
                  return { l, r };
                })
                .filter((x): x is { l: LinhaCalcPlanilha; r: NonNullable<typeof porLinha[string]> } => x != null);
              return (
                <div key={g.id} className="text-xs text-black">
                  <p className="font-semibold">
                    Grupo {g.ordem}: {g.rotulo}
                    {g.diaAlvo != null ? ` · igualado em ${fmt(g.diaAlvo, 1)} dias (quem já tem mais que isso não produz)` : ''}
                    {' · '}
                    {g.resultado ? fmt(g.resultado.total) : '—'} pts
                  </p>
                  <ul className="font-mono pl-2 space-y-0.5">
                    {dias.map(({ l, r }) => (
                      <li key={l.id}>
                        dia {r.diaAjustado != null ? fmt(r.diaAjustado, 1) : '—'}
                        {' · '}
                        qtd {r.quantidade}
                        {' · '}
                        pts {fmt(r.pontos)}
                        {' · '}
                        {rotuloCompletoLinhaCalc(l)}
                        {r.quantidade === 0 && (Number(l.vendas90) || 0) <= 0
                          ? ' (sem vendas 90d)'
                          : r.quantidade === 0 &&
                              g.diaAlvo != null &&
                              l.estSegur >= g.diaAlvo
                            ? ' (já acima do dia-alvo)'
                            : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {previa && idsSelecionadosLivres.length > 0 && (
              <p className="text-xs text-[#009b3a]">Prévia da seleção: {fmt(previa.total)} pts</p>
            )}
          </div>
          <p className="text-xs text-neutral-600">
            Iguala os dias do grupo. Se passar da meta, tira 5 de quem tem mais venda/qtd (o dia quase
            não cai). Quem ficou para trás (ex.: 6 dias) ganha essas 5.
          </p>
          <div className="mt-3 pt-3 border-t border-[#009b3a]/20">
            <div className="flex items-center justify-between gap-2 mb-1">
              <p className="text-xs font-semibold text-black">Resumo (nome + cor + código)</p>
              <button
                type="button"
                className="px-2 py-1 text-xs rounded-lg bg-[#e4e6ec] font-semibold"
                onClick={() => {
                  const blocos: string[] = [
                    `Soma grupos ${fmt(totalGrupos)}`,
                    ...gruposCalc.map(
                      (g) =>
                        `Grupo ${g.ordem} meta ${fmt(g.metaDoGrupo)} → ${g.resultado ? fmt(g.resultado.total) : '—'}`
                    ),
                    '',
                  ];
                  for (const g of gruposCalc) {
                    blocos.push(
                      `Grupo ${g.ordem}: ${g.rotulo}  ·  dias ${g.diaAlvo != null ? fmt(g.diaAlvo, 1) : '—'}  ·  total ${g.resultado ? fmt(g.resultado.total) : '—'}`
                    );
                    for (const l of linhas) {
                      if (!g.idsLinhas.includes(l.id)) continue;
                      const r = g.resultado?.porLinha[l.id];
                      if (!r || r.modo === 'fora') continue;
                      blocos.push(
                        `  dia ${r.diaAjustado != null ? fmt(r.diaAjustado, 1) : '—'}  |  qtd ${r.quantidade}  |  pts ${fmt(r.pontos)}  |  ${rotuloCompletoLinhaCalc(l)}`
                      );
                    }
                    blocos.push('');
                  }
                  const texto = blocos.join('\n').trim();
                  void navigator.clipboard.writeText(texto).then(
                    () => alert('Resumo copiado. Pode colar fora do sistema.'),
                    () => alert(texto)
                  );
                }}
              >
                Copiar resumo
              </button>
            </div>
            <ul className="text-xs text-black space-y-0.5 max-h-48 overflow-y-auto font-mono">
              {gruposCalc.map((g) => (
                <li key={g.id} className="font-semibold">
                  Grupo {g.ordem}: {g.rotulo} · {g.resultado ? fmt(g.resultado.total) : '—'} pts
                </li>
              ))}
              {linhas
                .filter((l) => {
                  const r = porLinha[l.id];
                  return r && r.modo !== 'fora' && r.quantidade > 0;
                })
                .map((l) => {
                  const r = porLinha[l.id];
                  const g = grupoPorLinha.get(l.id);
                  return (
                    <li key={l.id}>
                      {g ? `G${g.ordem} · ` : 'prévia · '}
                      dia {r?.diaAjustado != null ? fmt(r.diaAjustado, 1) : '—'} · qtd {r?.quantidade} · pts{' '}
                      {fmt(r?.pontos ?? 0)} · {rotuloCompletoLinhaCalc(l)}
                    </li>
                  );
                })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
